import { createSlice } from '@reduxjs/toolkit';
import theme from '../../../utils/themes';
import isArray from 'lodash/isArray';
import { STATUSES } from '../../../utils/Statuses';
import { ControlTypes } from '../../../utils/helper';
import { ensureFormPages, getQuestionPageId } from '../assets/formUtils';
import { remapLogicSourceOrders } from '../assets/formLogicUtils';

const formsInitialState = {
  form: {
    compFormSetting: {
      logo: null,
      banner: null,
      color: theme.primaryLight2,
      backgroundColor: theme.primary,
      fontFamily: 'Raleway',
      bodyFont: 'Raleway',
      baseTextSize: 13,
      backgroundGradient: '',
      logoUrl: '',
      bannerUrl: '',
      labelAlignment: 'left',
      customCss: '',
      formType: null,
      formViewType: 'standard',
      cardSurface: theme.white,
      formText: theme.headingColor,
      mutedText: theme.textColor,
      primaryText: theme.white,
      accent: theme.secondary,
      borders: theme.inputBorder,
      cornerRadius: 8,
      cardShadow: 'sm',
      questionSpacing: 20,
      buttonStyle: 'solid',
      fieldStyle: 'outline',
      showProgress: true,
    },
    refOpts: {
      isAccepting: true,
      expireAfter: 21,
    },
    sections: [],
    pages: [],
  },
};

const withQuestionIndex = (questions = []) => {
  const orderMap = new Map();
  questions.forEach((question, index) => {
    if (question?.sectionOrder != null && question.sectionOrder !== '') {
      orderMap.set(Number(question.sectionOrder), index + 1);
    }
  });

  let sequence = 0;
  return questions.map((question, index) => {
    let sectionOptions = question?.sectionOptions;
    if (sectionOptions?.logic) {
      const logic = remapLogicSourceOrders(
        sectionOptions.logic,
        orderMap,
        index + 1,
      );
      sectionOptions = { ...sectionOptions };
      if (logic) sectionOptions.logic = logic;
      else delete sectionOptions.logic;
    }

    return {
      ...question,
      ...(question?.id == null && !question?.clientId
        ? { clientId: `tmp-${index + 1}-${question?.sectionType || 'q'}` }
        : {}),
      ...(sectionOptions ? { sectionOptions } : {}),
      index:
        question.sectionType === ControlTypes.SECTION
          ? `s-${sequence}`
          : ++sequence,
      sectionOrder: index + 1,
      ...(question?.controls &&
        isArray(question.controls) && {
          controls: question.controls.map((control) => ({
            ...control,
            index: ++sequence,
          })),
        }),
    };
  });
};

const slice = createSlice({
  name: 'formDesign',
  initialState: { ...formsInitialState },
  reducers: {
    setFormData: (state, { payload: { form } }) => {
      const live = form?.formQuestions || [];
      const pages = ensureFormPages(form);
      // Preserve caller array order (drag/reorder). withQuestionIndex renumbers
      // sectionOrder from that order — sorting here would undo UI reorders.
      const formQuestions = withQuestionIndex([
        ...live.filter(
          (question) =>
            question.statusId !== STATUSES.DELETE && !question?.isSensitive,
        ),
        ...live.filter((question) => question?.isSensitive),
      ]).map((question) => ({
        ...question,
        pageId: question?.isSensitive
          ? question.pageId
          : getQuestionPageId(question, pages),
      }));

      state.form = { ...form, pages, formQuestions };
    },
    resetFormData: (state) => {
      state.form = { ...formsInitialState.form };
    },
  },
});

export const { setFormData, resetFormData } = slice.actions;

export default slice.reducer;

export const selectCandidate = (state) => state.candidates.activeCandidate;

export const selectForm = (state) => state.formDesign.form;
